import React, { useEffect, useState } from "react";
import { SlDialog, SlButton } from "@shoelace-style/shoelace/dist/react";
import Datepicker from "react-tailwindcss-datepicker";
import { fetchEvents, fetchSearchEvents, fetchEventsCsv, fetchSearchEventsCsv } from "./api";
import Paginator from "../../shared/Paginator";
import TableLoader from "../../shared/TableLoader";
import NoDataFound from "../../shared/NoDataFound";
import CsvExportButton from "./CsvExportButton";
import { formatTimestamp, formatEventProps } from "./format";

const PAGE_SIZE = 20;
const thCls = "px-4 py-3 border border-gray-200 font-semibold whitespace-nowrap";
const tdCls = "px-4 py-3 border border-gray-200 align-top";

// Shared by both tabs: the paginated event list for one (site_id, cust_id,
// event_name) triple, with its own date filter and order control.
function EventsModal({ open, siteId, custId, eventName, searchTerm, onClose }) {
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
  const [order, setOrder] = useState("desc");
  const [page, setPage] = useState(0);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // Reset the modal's own filters each time it's opened for a new triple.
  useEffect(() => {
    if (!open) return;
    setDateRange({ startDate: null, endDate: null });
    setOrder("desc");
    setPage(0);
  }, [open, siteId, custId, eventName, searchTerm]);

  const load = async (pageArg, orderArg, dateRangeArg) => {
    setLoading(true);
    try {
      const params = {
        page: pageArg + 1,
        dateFrom: dateRangeArg?.startDate || undefined,
        dateTo: dateRangeArg?.endDate || undefined,
        order: orderArg,
      };
      const res = searchTerm
        ? await fetchSearchEvents(siteId, custId, searchTerm, params)
        : await fetchEvents(siteId, custId, eventName, params);
      if (res.code === 200) {
        setItems(res.data?.items || []);
        setTotal(res.data?.total || 0);
      } else {
        setItems([]);
        setTotal(0);
      }
    } catch (err) {
      console.error(err);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !siteId || !custId || !(eventName || searchTerm)) return;
    load(page, order, dateRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, siteId, custId, eventName, searchTerm, page, order, dateRange?.startDate, dateRange?.endDate]);

  const handlePageClick = (e) => setPage(e.selected);
  const handleOrderChange = (val) => {
    setOrder(val);
    setPage(0);
  };
  const handleDateRangeChange = (val) => {
    setDateRange(val);
    setPage(0);
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const startIndex = page * PAGE_SIZE;
  const csvParams = { dateFrom: dateRange?.startDate || undefined, dateTo: dateRange?.endDate || undefined, order };
  const fetchCsv = () =>
    searchTerm
      ? fetchSearchEventsCsv(siteId, custId, searchTerm, csvParams)
      : fetchEventsCsv(siteId, custId, eventName, csvParams);

  return (
    <SlDialog
      label={searchTerm ? `Events — alt/title/text contains "${searchTerm}"` : eventName ? `Events — ${eventName}` : "Events"}
      open={open}
      style={{ "--width": "min(1400px, 80vw)" }}
      onSlRequestClose={(e) => {
        if (e.detail.source === "overlay") e.preventDefault();
      }}
      onSlAfterHide={(e) => {
        // Only the dialog's own hide event, not one bubbling up from a
        // nested element (e.g. the datepicker's popover).
        if (e.target !== e.currentTarget) return;
        onClose();
      }}
    >
      {siteId && custId && (eventName || searchTerm) && (
        <>
          <div className="flex flex-wrap items-end justify-between gap-3 mb-3 text-sm text-gray-600">
            <div>
              <span className="font-semibold text-gray-700">Site:</span> {siteId}
              <span className="font-semibold text-gray-700 ml-3">Cust:</span> {custId}
            </div>
            <div className="flex items-end gap-3">
              <CsvExportButton fetchRows={fetchCsv} filename="events.csv" />
              <div className="min-w-[220px]">
                <label className="mb-1 text-xs font-semibold text-gray-500 block">Date Range</label>
                <div className="border rounded-lg border-gray-300">
                  <Datepicker
                    value={dateRange}
                    onChange={handleDateRangeChange}
                    primaryColor="cyan"
                    showShortcuts
                    asSingle={false}
                    placeholder="All time"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1 text-xs font-semibold text-gray-500 block">Order</label>
                <select
                  className="border rounded px-2 py-2 text-sm"
                  value={order}
                  onChange={(e) => handleOrderChange(e.target.value)}
                >
                  <option value="desc">Newest First</option>
                  <option value="asc">Oldest First</option>
                </select>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto max-h-[55vh] overflow-y-auto border border-gray-200 rounded-lg">
            <table className="min-w-full text-base text-gray-800 border-collapse">
              <thead className="bg-gray-50 text-sm uppercase text-gray-600 sticky top-0">
                <tr>
                  <th className={`${thCls} text-right w-12`}>#</th>
                  <th className={`${thCls} text-left`}>Client Time (IST)</th>
                  <th className={`${thCls} text-left`}>Received (IST)</th>
                  <th className={`${thCls} text-left`}>URL</th>
                  <th className={`${thCls} text-left`}>Details</th>
                </tr>
              </thead>
              {loading ? (
                <TableLoader colspan="5" />
              ) : items.length ? (
                <tbody>
                  {items.map((ev, idx) => (
                    <tr key={ev._id} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                      <td className={`${tdCls} text-right text-gray-400`}>{startIndex + idx + 1}</td>
                      <td className={`${tdCls} whitespace-nowrap`}>{formatTimestamp(ev.client_ts_ist)}</td>
                      <td className={`${tdCls} whitespace-nowrap`}>{formatTimestamp(ev.received_at_ist)}</td>
                      <td className={`${tdCls} break-all max-w-xs`}>
                        <a href={ev.url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                          {ev.url}
                        </a>
                      </td>
                      <td className={tdCls}>
                        <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1">
                          {formatEventProps(ev.event_name, ev.props).map((row, i) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <React.Fragment key={i}>
                              <span className="text-sm font-semibold text-gray-500 whitespace-nowrap">{row.label}:</span>
                              {row.link ? (
                                <a
                                  href={row.value}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-sm text-blue-600 hover:underline break-all"
                                >
                                  {row.value}
                                </a>
                              ) : (
                                <span className="text-sm text-gray-800 break-all">{row.value}</span>
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              ) : (
                <NoDataFound colspan="5" />
              )}
            </table>
          </div>

          {totalPages > 1 && (
            <div className="mt-3">
              <Paginator
                pageCount={totalPages}
                handlePageClick={handlePageClick}
                forcePage={page}
                totalItems={total}
                pageSize={PAGE_SIZE}
              />
            </div>
          )}
        </>
      )}
      <SlButton slot="footer" variant="primary" onClick={onClose}>
        Close
      </SlButton>
    </SlDialog>
  );
}

export default EventsModal;
